import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Stepper, Step, StepLabel,
  TextField, Button, Alert, Chip, CircularProgress,
  Divider, Switch, FormControlLabel, Grid,
  List, ListItem, ListItemIcon, ListItemText,
  Table, TableBody, TableRow, TableCell, Tooltip, IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StorageIcon       from '@mui/icons-material/Storage';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import ErrorIcon         from '@mui/icons-material/Error';
import InfoIcon          from '@mui/icons-material/Info';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon  from '@mui/icons-material/ArrowForward';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import SendIcon          from '@mui/icons-material/Send';
import RefreshIcon       from '@mui/icons-material/Refresh';
import CloudUploadIcon   from '@mui/icons-material/CloudUpload';
import ContentCopyIcon   from '@mui/icons-material/ContentCopy';
import DownloadIcon      from '@mui/icons-material/Download';
import TerminalIcon      from '@mui/icons-material/Terminal';
import { useNavigate }   from 'react-router-dom';
import api               from '../utils/api';

// ── Helpers ──────────────────────────────────────────────────────────────────
const toSlug = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 40);

const toPascal = (str) =>
  str
    .replace(/[^a-zA-Z0-9 ]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');

const STEPS = ['VM Connection', 'Tenant Identity', 'Database & Options', 'Review', 'Provisioning'];

const PROVISION_STEPS_DESC = [
  'Test SSH connection',
  'Sync app code to tenant server',
  'npm install --omit=dev',
  'Create web root directory',
  'Copy React build',
  'Generate .env with secrets',
  'Write nginx config',
  'Disable default nginx site',
  'Enable new site',
  'Test & reload nginx',
  'Run certbot / SSL',
  'Start app with PM2',
  'Health check',
  'Run database migrations',
  'Register tenant in admin panel',
];

// ── Log entry component ───────────────────────────────────────────────────────
const LOG_COLORS = {
  step:  '#60a5fa',   // blue
  ok:    '#4ade80',   // green
  error: '#f87171',   // red
  warn:  '#fbbf24',   // amber
  info:  '#94a3b8',   // slate
};
const LOG_PREFIX = {
  step:  '▶ ',
  ok:    '✓ ',
  error: '✗ ',
  warn:  '⚠ ',
  info:  '  ',
};

function LogEntry({ entry }) {
  const color = LOG_COLORS[entry.level] || LOG_COLORS.info;
  return (
    <Box component="div" sx={{ display: 'flex', gap: 0.5, py: 0.2, lineHeight: 1.5 }}>
      <Box component="span" sx={{ color, fontFamily: 'monospace', fontSize: entry.level === 'step' ? '0.8rem' : '0.72rem', fontWeight: entry.level === 'step' ? 700 : 400, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
        <span style={{ opacity: 0.7 }}>{LOG_PREFIX[entry.level] || '  '}</span>
        <span style={{ color }}>{entry.message}</span>
      </Box>
    </Box>
  );
}

// ── Recent provision jobs ─────────────────────────────────────────────────────
function RecentJobs({ onRefresh }) {
  const theme = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/provision/jobs');
      setJobs((data || []).slice(0, 5));
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, onRefresh]);

  const statusColor = (s) =>
    s === 'success' ? '#4caf50' : s === 'failed' ? '#f44336' : theme.palette.primary.main;

  return (
    <Paper sx={{ p: 2, mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Recent Provisions</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : jobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No provision jobs yet.</Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {jobs.map(j => (
              <TableRow key={j.id}>
                <TableCell>
                  <Chip
                    label={j.status}
                    size="small"
                    sx={{ bgcolor: statusColor(j.status), color: '#fff', fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell><Typography variant="body2">{j.tenant_name}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{j.domain}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{j.ssh_host}</Typography></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {j.created_at ? new Date(j.created_at + 'Z').toLocaleString() : ''}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Provision() {
  const theme    = useTheme();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [refreshJobs, setRefreshJobs] = useState(0);

  // Form state
  const [form, setForm] = useState({
    ssh_host: '',
    ssh_port: '22',
    ssh_user: 'root',
    name: '',
    slug: '',
    domain: '',
    webroot: '',
    certbot_email: '',
    db1_server: '',
    db1_name: '',
    db1_user: '',
    db1_password: '',
    useDb2: false,
    db2_server: '',
    db2_port: '1435',
    db2_name: '',
    db2_user: '',
    db2_password: '',
    skip_certbot: false,
    skip_migrations: false,
  });

  // Provisioning state
  const [jobId,  setJobId]  = useState(null);
  const [job,    setJob]    = useState(null);
  const [logs,   setLogs]   = useState([]);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [setupScript, setSetupScript] = useState('');
  const [scriptCopied, setScriptCopied] = useState(false);

  useEffect(() => {
    api.get('/provision/setup-script').then(r => setSetupScript(r.data)).catch(() => {});
  }, []);

  const copyScript = () => {
    navigator.clipboard.writeText(setupScript);
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  };

  const downloadScript = () => {
    const blob = new Blob([setupScript], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'hoa-preflight.sh';
    a.click();
  };
  const logRef = useRef(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleNameChange = (val) => {
    setForm(f => ({
      ...f,
      name: val,
      slug: toSlug(val),
      webroot: toPascal(val),
    }));
  };

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    if (job && (job.status === 'success' || job.status === 'failed')) return;

    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/provision/jobs/${jobId}`);
        setJob(data.job);
        setLogs(data.logs || []);
      } catch {}
    }, 2000);

    return () => clearInterval(interval);
  }, [jobId, job]);

  // Auto-scroll logs
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  const handleStart = async () => {
    setStarting(true);
    setStartError('');
    try {
      const payload = {
        ssh_host:         form.ssh_host,
        ssh_port:         Number(form.ssh_port) || 22,
        ssh_user:         form.ssh_user || 'root',
        name:             form.name,
        slug:             form.slug,
        domain:           form.domain,
        webroot:          form.webroot,
        certbot_email:    form.certbot_email,
        db1_server:       form.db1_server,
        db1_name:         form.db1_name,
        db1_user:         form.db1_user,
        db1_password:     form.db1_password,
        db2_server:       form.useDb2 ? form.db2_server : '',
        db2_port:         form.useDb2 ? Number(form.db2_port) || 1435 : 1435,
        db2_name:         form.useDb2 ? form.db2_name : '',
        db2_user:         form.useDb2 ? form.db2_user : '',
        db2_password:     form.useDb2 ? form.db2_password : '',
        skip_certbot:     form.skip_certbot,
        skip_migrations:  form.skip_migrations,
      };
      const data = await api.post('/provision', payload);
      setJobId(data.jobId);
      setJob({ status: 'running' });
      setLogs([]);
    } catch (e) {
      setStartError(e.message || 'Failed to start provisioning');
    }
    setStarting(false);
  };

  // ── Step renderers ────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enter the SSH connection details for the target VM.
      </Typography>
      <TextField
        label="SSH Host (IP Address)"
        value={form.ssh_host}
        onChange={e => set('ssh_host', e.target.value)}
        placeholder="1.2.3.4"
        fullWidth required
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="SSH Port"
            value={form.ssh_port}
            onChange={e => set('ssh_port', e.target.value)}
            type="number"
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="SSH User"
            value={form.ssh_user}
            onChange={e => set('ssh_user', e.target.value)}
            fullWidth
          />
        </Grid>
      </Grid>
      {/* Pre-flight script panel */}
      <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, backgroundColor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
          <TerminalIcon fontSize="small" color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>Pre-flight Script</Typography>
            <Typography variant="caption" color="text.secondary">
              Run this on the new server <strong>before</strong> provisioning. It authorizes the deploy key, opens firewall ports, and installs nginx / certbot / Node / PM2.
            </Typography>
          </Box>
          <Tooltip title={scriptCopied ? 'Copied!' : 'Copy script'}>
            <IconButton size="small" onClick={copyScript} color={scriptCopied ? 'success' : 'default'}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download as .sh file">
            <IconButton size="small" onClick={downloadScript}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          component="pre"
          sx={{
            m: 0, p: 2,
            fontSize: '0.72rem',
            fontFamily: 'monospace',
            lineHeight: 1.6,
            backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : '#1e1e2e',
            color: '#a6e3a1',
            overflowX: 'auto',
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {setupScript || 'Loading...'}
        </Box>
        <Box sx={{ px: 2, py: 1, backgroundColor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Run as root: <code style={{ fontFamily: 'monospace' }}>curl -s https://admin.myhoa.club/api/provision/setup-script | bash</code>
            {' '}&nbsp;or copy and paste above.
          </Typography>
        </Box>
      </Paper>

      <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '10px' }}>
        After running the script, enter the server IP below and continue to the next step.
      </Alert>
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configure the tenant's identity. Slug and Web Root are auto-generated from the name.
      </Typography>
      <TextField
        label="Tenant Name"
        value={form.name}
        onChange={e => handleNameChange(e.target.value)}
        placeholder="Sunset HOA"
        fullWidth required
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Slug"
            value={form.slug}
            onChange={e => set('slug', e.target.value)}
            helperText="Unique identifier, no spaces"
            fullWidth required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Web Root Directory"
            value={form.webroot}
            onChange={e => set('webroot', e.target.value)}
            helperText={`Served from /var/www/${form.webroot || '...'}`}
            fullWidth required
          />
        </Grid>
      </Grid>
      <TextField
        label="Domain"
        value={form.domain}
        onChange={e => set('domain', e.target.value)}
        placeholder="sunset.myhoa.club"
        helperText="Must point to the target VM's IP before certbot runs"
        fullWidth required
      />
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">Primary Database (DB1) — Required</Typography>
      <Grid container spacing={2}>
        <Grid item xs={8}><TextField label="DB1 Server" value={form.db1_server} onChange={e => set('db1_server', e.target.value)} fullWidth /></Grid>
        <Grid item xs={4}><TextField label="DB1 Name" value={form.db1_name} onChange={e => set('db1_name', e.target.value)} fullWidth /></Grid>
        <Grid item xs={6}><TextField label="DB1 User" value={form.db1_user} onChange={e => set('db1_user', e.target.value)} fullWidth /></Grid>
        <Grid item xs={6}><TextField label="DB1 Password" value={form.db1_password} onChange={e => set('db1_password', e.target.value)} type="password" fullWidth /></Grid>
      </Grid>

      <Divider />

      <FormControlLabel
        control={<Switch checked={form.useDb2} onChange={e => set('useDb2', e.target.checked)} />}
        label="Enable Datamart (DB2)"
      />

      {form.useDb2 && (
        <>
          <Typography variant="subtitle2" color="text.secondary">Datamart (DB2) — Optional</Typography>
          <Grid container spacing={2}>
            <Grid item xs={8}><TextField label="DB2 Server" value={form.db2_server} onChange={e => set('db2_server', e.target.value)} fullWidth /></Grid>
            <Grid item xs={4}><TextField label="DB2 Port" value={form.db2_port} onChange={e => set('db2_port', e.target.value)} type="number" fullWidth /></Grid>
            <Grid item xs={4}><TextField label="DB2 Name" value={form.db2_name} onChange={e => set('db2_name', e.target.value)} fullWidth /></Grid>
            <Grid item xs={4}><TextField label="DB2 User" value={form.db2_user} onChange={e => set('db2_user', e.target.value)} fullWidth /></Grid>
            <Grid item xs={4}><TextField label="DB2 Password" value={form.db2_password} onChange={e => set('db2_password', e.target.value)} type="password" fullWidth /></Grid>
          </Grid>
        </>
      )}

      <Divider />

      <TextField
        label="Certbot Email"
        value={form.certbot_email}
        onChange={e => set('certbot_email', e.target.value)}
        placeholder="admin@myhoa.club"
        helperText="Required unless 'Skip SSL' is checked"
        fullWidth
      />

      <Box sx={{ display: 'flex', gap: 4 }}>
        <FormControlLabel
          control={<Switch checked={form.skip_certbot} onChange={e => set('skip_certbot', e.target.checked)} />}
          label="Skip SSL (certbot)"
        />
        <FormControlLabel
          control={<Switch checked={form.skip_migrations} onChange={e => set('skip_migrations', e.target.checked)} />}
          label="Skip migrations"
        />
      </Box>
    </Box>
  );

  const mask = (v) => v ? '••••••••' : '(empty)';

  const renderStep4 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="warning" icon={<WarningAmberIcon />}>
        This will SSH into <strong>{form.ssh_host}</strong> and modify it. Review all settings carefully before proceeding.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>VM Connection</Typography>
        <Table size="small">
          <TableBody>
            {[
              ['SSH Host', form.ssh_host],
              ['SSH Port', form.ssh_port],
              ['SSH User', form.ssh_user],
            ].map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.5, pl: 0 }}>{k}</TableCell>
                <TableCell sx={{ border: 0, py: 0.5 }}>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>Tenant Identity</Typography>
        <Table size="small">
          <TableBody>
            {[
              ['Name', form.name],
              ['Slug', form.slug],
              ['Domain', form.domain],
              ['Web Root', `/var/www/${form.webroot}`],
            ].map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.5, pl: 0 }}>{k}</TableCell>
                <TableCell sx={{ border: 0, py: 0.5 }}>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>Database</Typography>
        <Table size="small">
          <TableBody>
            {[
              ['DB1 Server', form.db1_server],
              ['DB1 Name', form.db1_name],
              ['DB1 User', form.db1_user],
              ['DB1 Password', mask(form.db1_password)],
              ...(form.useDb2 ? [
                ['DB2 Server', form.db2_server],
                ['DB2 Port', form.db2_port],
                ['DB2 Name', form.db2_name],
                ['DB2 User', form.db2_user],
                ['DB2 Password', mask(form.db2_password)],
              ] : [['Datamart (DB2)', 'Disabled']]),
            ].map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ color: 'text.secondary', border: 0, py: 0.5, pl: 0 }}>{k}</TableCell>
                <TableCell sx={{ border: 0, py: 0.5 }}>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>Options</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Certbot: ${form.skip_certbot ? 'SKIP' : 'Run'}`} size="small" color={form.skip_certbot ? 'default' : 'success'} />
          <Chip label={`Migrations: ${form.skip_migrations ? 'SKIP' : 'Run'}`} size="small" color={form.skip_migrations ? 'default' : 'success'} />
          {form.certbot_email && <Chip label={`Email: ${form.certbot_email}`} size="small" />}
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>What will happen</Typography>
        <List dense disablePadding>
          {PROVISION_STEPS_DESC.map((desc, i) => (
            <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{i + 1}.</Typography>
              </ListItemIcon>
              <ListItemText primary={<Typography variant="body2">{desc}</Typography>} />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );

  const renderStep5 = () => {
    const isRunning = job && job.status === 'running';
    const isSuccess = job && job.status === 'success';
    const isFailed  = job && job.status === 'failed';

    // Derive which provision steps are done/active/pending from logs
    const stepLogs = logs.filter(l => l.level === 'step');
    const errorLogs = logs.filter(l => l.level === 'error');
    const currentStepIdx = stepLogs.length > 0 ? stepLogs.length - 1 : -1;

    // Check if any error happened after last step header
    const lastStepLogId = stepLogs.length > 0 ? stepLogs[stepLogs.length - 1].id : 0;
    const hasCurrentStepError = errorLogs.some(e => e.id > lastStepLogId);

    const STEPS_LIST = [
      'Test SSH connection',
      'Sync app code',
      'npm install',
      'Create web directory',
      'Copy React build',
      'Generate .env',
      'Rename DB to MyHOA',
      'Write nginx config',
      'Disable default site',
      'Enable new site',
      'Test & reload nginx',
      'SSL certificate (certbot)',
      'Start app with PM2',
      'Health check',
      'Run migrations',
      'Register tenant',
    ];

    const getStepState = (i) => {
      if (!jobId && !starting) return 'pending';
      if (isSuccess) return 'done';
      if (i < currentStepIdx) return 'done';
      if (i === currentStepIdx) {
        if (isFailed && hasCurrentStepError) return 'error';
        return 'active';
      }
      return 'pending';
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Status banner */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
          p: 2, borderRadius: '12px',
          backgroundColor: isSuccess ? 'rgba(22,163,74,0.08)' : isFailed ? 'rgba(220,38,38,0.08)' : 'action.hover',
          border: '1px solid', borderColor: isSuccess ? 'rgba(22,163,74,0.3)' : isFailed ? 'rgba(220,38,38,0.3)' : 'divider',
        }}>
          {(isRunning || starting) && <CircularProgress size={22} />}
          {isSuccess && <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 28 }} />}
          {isFailed  && <ErrorIcon sx={{ color: '#dc2626', fontSize: 28 }} />}
          {!job && !starting && <InfoIcon color="action" fontSize="large" />}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {starting  ? 'Starting...' :
               isRunning && currentStepIdx >= 0 ? `Step ${currentStepIdx + 1} of ${STEPS_LIST.length}: ${STEPS_LIST[currentStepIdx]}` :
               isRunning ? 'Connecting to server...' :
               isSuccess ? 'Provisioning complete!' :
               isFailed  ? 'Provisioning failed' :
               'Ready to provision'}
            </Typography>
            {isRunning && currentStepIdx >= 0 && (
              <Typography variant="caption" color="text.secondary">
                {STEPS_LIST.length - currentStepIdx - 1} steps remaining
              </Typography>
            )}
          </Box>
          {isRunning && currentStepIdx >= 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {currentStepIdx + 1}/{STEPS_LIST.length}
            </Typography>
          )}
        </Box>

        {startError && <Alert severity="error" sx={{ borderRadius: '10px' }}>{startError}</Alert>}

        {!jobId && !starting && (
          <Button variant="contained" size="large" startIcon={<SendIcon />} onClick={handleStart} disabled={starting}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, py: 1.5 }}>
            Start Provisioning
          </Button>
        )}

        {/* Two-column: step tracker + live log */}
        {(jobId || starting) && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

            {/* Step tracker */}
            <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden', minWidth: 220, flexShrink: 0 }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.65rem' }}>
                  Progress
                </Typography>
              </Box>
              <Box sx={{ py: 0.5 }}>
                {STEPS_LIST.map((label, i) => {
                  const state = getStepState(i);
                  return (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      px: 1.5, py: 0.6,
                      backgroundColor: state === 'active' ? 'rgba(59,130,246,0.08)' : 'transparent',
                      borderLeft: state === 'active' ? '3px solid #3b82f6' : '3px solid transparent',
                      transition: 'all 0.2s',
                    }}>
                      <Box sx={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {state === 'done'    && <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />}
                        {state === 'active'  && <CircularProgress size={14} thickness={5} />}
                        {state === 'error'   && <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />}
                        {state === 'pending' && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'action.disabled', border: '1px solid', borderColor: 'divider' }} />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{
                        fontSize: '0.76rem',
                        fontWeight: state === 'active' ? 700 : 400,
                        color: state === 'done' ? 'text.secondary' : state === 'active' ? 'text.primary' : state === 'error' ? '#dc2626' : 'text.disabled',
                        lineHeight: 1.3,
                      }}>
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* Live log */}
            <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden', flex: 1, minWidth: 0 }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.65rem' }}>
                  Live Output
                </Typography>
                {isRunning && <Chip label="LIVE" size="small" color="success" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, animation: 'pulse 2s infinite' }} />}
              </Box>
              <Box
                ref={logRef}
                sx={{
                  p: 1.5, maxHeight: 380, overflowY: 'auto',
                  backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : '#1a1a2e',
                  fontFamily: 'monospace',
                }}
              >
                {logs.length === 0 ? (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>Waiting for output...</Typography>
                ) : (
                  logs.map(entry => <LogEntry key={entry.id} entry={entry} />)
                )}
                {isRunning && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16a34a', animation: 'pulse 1s infinite' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: '0.7rem' }}>running...</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Success */}
        {isSuccess && (
          <Alert severity="success" sx={{ borderRadius: '10px' }}
            action={<Button color="inherit" size="small" fontWeight={700} onClick={() => { setRefreshJobs(r => r + 1); navigate('/tenants'); }}>View Tenants</Button>}>
            Tenant registered! HOA is live at <strong>{form.skip_certbot ? `http://${form.ssh_host}:3200` : `https://${form.domain}`}</strong>
          </Alert>
        )}

        {/* Failure */}
        {isFailed && (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            <strong>Failed:</strong> {job.error || 'Unknown error'} — check the log for details.
          </Alert>
        )}
      </Box>
    );
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const canNext = () => {
    switch (activeStep) {
      case 0: return form.ssh_host.trim() !== '';
      case 1: return form.name && form.slug && form.domain && form.webroot;
      case 2: return form.db1_server && form.db1_name && form.db1_user;
      case 3: return true;
      default: return false;
    }
  };

  const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', px: 2, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <CloudUploadIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Provision</Typography>
          <Typography variant="body2" color="text.secondary">
            Deploy HOA to a new tenant server from snapshot
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step content */}
      <Paper sx={{ p: 3, mb: 2 }}>
        {stepContent[activeStep]()}
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setActiveStep(s => s - 1)}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        {activeStep < STEPS.length - 1 && (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => setActiveStep(s => s + 1)}
            disabled={!canNext()}
          >
            {activeStep === STEPS.length - 2 ? 'Begin Provisioning' : 'Next'}
          </Button>
        )}
      </Box>

      {/* Recent provisions */}
      <RecentJobs onRefresh={refreshJobs} />
    </Box>
  );
}
